import { useState } from "react";
import { motion } from "framer-motion";
import { X, Loader2, Copy, Check, Play, Braces } from "lucide-react";

/**
 * Modal de documentação (estilo Swagger) do endpoint /api/buscar_endereco.
 * Inclui descrição dos campos, exemplo e um "Experimentar" ao vivo.
 */
export default function ApiDocsModal({ api, onClose }) {
  const endpoint = `${api}/buscar_endereco`;
  const [endereco, setEndereco] = useState(
    "Avenida Paulista 1578, São Paulo"
  );
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erroMsg, setErroMsg] = useState(null);
  const [copiado, setCopiado] = useState(false);

  const exemploURL = `${endpoint}?endereco=${encodeURIComponent(
    "Avenida Paulista 1578, São Paulo"
  )}`;

  const copiar = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1600);
    } catch (e) {
      /* noop */
    }
  };

  const testar = async () => {
    const termo = endereco.trim();
    if (!termo) return;
    setLoading(true);
    setErroMsg(null);
    setResultado(null);
    try {
      const r = await fetch(
        `${endpoint}?endereco=${encodeURIComponent(termo)}`
      );
      const data = await r.json();
      setResultado(JSON.stringify(data, null, 2));
    } catch (e) {
      setErroMsg("Falha ao chamar a API. Verifique o endereço e tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const campos = [
    ["sucesso", "boolean", "Indica se a busca encontrou o endereço."],
    ["mensagem", "string", "Mensagem de erro/aviso (quando algo não foi encontrado)."],
    ["endereco_buscado", "string", "O termo exatamente como você enviou."],
    ["endereco_completo", "string", "Endereço completo formatado (bairro, cidade, CEP...)."],
    ["localizacao.latitude", "number", "Latitude do local."],
    ["localizacao.longitude", "number", "Longitude do local."],
    ["detalhes.nome", "string", "Nome do logradouro/local."],
    ["detalhes.tipo", "string", "Tipo do local (ex.: residential, bus_stop)."],
    ["detalhes.categoria", "string", "Categoria (ex.: highway)."],
    ["detalhes.caixa_delimitadora", "number[]", "Bounding box [sul, norte, oeste, leste]."],
    ["detalhes.logradouro", "string", "Rua/avenida do endereço."],
    ["detalhes.numero", "string", "Número do imóvel (quando disponível)."],
    ["detalhes.bairro", "string", "Bairro/distrito."],
    ["detalhes.cidade", "string", "Cidade."],
    ["detalhes.municipio", "string", "Município."],
    ["detalhes.estado", "string", "Estado/UF por extenso (ex.: Goiás)."],
    ["detalhes.estado_sigla", "string", "Sigla do estado (ex.: GO)."],
    ["detalhes.regiao", "string", "Região (ex.: Região Centro-Oeste)."],
    ["detalhes.cep", "string", "CEP/Código postal."],
    ["detalhes.pais", "string", "País por extenso."],
    ["detalhes.pais_codigo", "string", "Código do país (ISO, ex.: BR)."],
    ["google.place_id", "string", "Place ID do Google (formato ChIJ...)."],
    ["google.cid", "string", "CID do Google (formato 0x..:0x..)."],
    ["google.fid", "string", "Feature ID do Google (formato /g/...)."],
    ["google.nome", "string", "Nome/título do local segundo o Google (ex.: edifício)."],
    ["google.endereco_formatado", "string", "Endereço formatado exatamente como o Google exibe."],
    ["google.cep", "string", "CEP preciso retornado pelo Google."],
    ["google.cidade", "string", "Cidade segundo o Google."],
    ["google.estado", "string", "Estado segundo o Google."],
    ["google.pais_codigo", "string", "Código do país segundo o Google (ex.: BR)."],
    ["google.fuso_horario", "string", "Fuso horário (ex.: America/Sao_Paulo)."],
    ["google.maps_url", "string", "Link direto para o local no Google Maps."],
    ["street_view.disponivel", "boolean", "Se há Street View neste ponto."],
    ["street_view.pano_id", "string", "ID do panorama do Google."],
    ["street_view.direcao_fachada_graus", "number", "Ângulo (graus) apontando para a fachada."],
    ["street_view.direcao_camera_graus", "number", "Ângulo (graus) original da câmera."],
    ["street_view.data_captura", "string", "Mês/ano da captura (ex.: 2024-10)."],
    ["street_view.zoom_maximo", "number", "Nível máximo de zoom do panorama."],
    ["street_view.imagem_fachada_url", "string", "URL da foto da fachada (JPEG)."],
    ["street_view.imagem_360_url", "string", "URL do panorama equiretangular 360° (JPEG)."],
    ["street_view.renderizar_360_url", "string", "URL da PÁGINA com o 360 interativo já renderizado (arrastar o mouse, olhar em volta, tela cheia) — abra no navegador ou embuta em iframe. Com &formato=imagem devolve o JPEG cacheado."],
    ["street_view.historico", "array", "Linha do tempo do local: captura atual + capturas antigas (2023, 2022...), da mais recente para a mais antiga."],
    ["street_view.historico[].data_captura", "string", "Mês/ano daquela captura (ex.: 2023-01)."],
    ["street_view.historico[].ano", "number", "Ano da captura (ex.: 2023)."],
    ["street_view.historico[].atual", "boolean", "true = captura mais recente (a exibida por padrão)."],
    ["street_view.historico[].pano_id", "string", "ID do panorama daquela época."],
    ["street_view.historico[].direcao_fachada_graus", "number", "Ângulo apontando para a fachada naquela época."],
    ["street_view.historico[].imagem_fachada_url", "string", "URL da foto da fachada daquele ano (JPEG)."],
    ["street_view.historico[].imagem_360_url", "string", "URL do panorama 360° daquele ano (JPEG)."],
    ["street_view.historico[].renderizar_360_url", "string", "URL da página com o 360 interativo daquele ano (visualizador navegável, pronto para iframe)."],
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm sm:p-8"
      data-testid="api-docs-modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.98 }}
        transition={{ type: "spring", stiffness: 240, damping: 26 }}
        className="glass relative w-full max-w-2xl rounded-2xl shadow-2xl shadow-black/60"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-white/10 px-5 py-4">
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-teal-400/15 text-teal-300">
              <Braces className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-display text-base font-bold text-white">
                API · Buscar Endereço
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-xl text-slate-300 transition-colors hover:bg-white/10 hover:text-rose-300"
            data-testid="api-docs-close-btn"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[75vh] space-y-5 overflow-y-auto px-5 py-5">
          {/* Endpoint */}
          <section>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-emerald-400/15 px-2 py-0.5 text-xs font-bold text-emerald-300">
                GET
              </span>
              <code className="min-w-0 flex-1 truncate rounded-lg bg-black/40 px-3 py-1.5 font-mono text-xs text-teal-200">
                {endpoint}?endereco=&lt;endereço&gt;
              </code>
              <button
                onClick={() => copiar(exemploURL)}
                className="flex items-center gap-1 rounded-lg bg-white/10 px-2.5 py-1.5 text-xs text-slate-200 transition-colors hover:bg-white/15"
              >
                {copiado ? (
                  <Check className="h-3.5 w-3.5 text-emerald-300" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
                {copiado ? "Copiado" : "Copiar"}
              </button>
            </div>
          </section>

          {/* Parâmetros */}
          <section>
            <h3 className="mb-2 text-sm font-bold text-white">Parâmetros</h3>
            <div className="overflow-hidden rounded-xl border border-white/10">
              <table className="w-full text-left text-sm">
                <tbody>
                  <tr className="border-b border-white/10">
                    <td className="px-3 py-2 font-mono text-teal-200">endereco</td>
                    <td className="px-3 py-2 text-slate-400">string · obrigatório</td>
                    <td className="px-3 py-2 text-slate-300">
                      O endereço a buscar. Também aceita coordenadas para busca
                      reversa (ex.:{" "}
                      <code className="rounded bg-black/40 px-1 font-mono text-[11px] text-teal-200">
                        -18.3976961, -49.2372227
                      </code>
                      ) — o backend resolve rua, número, CEP e Street View pela
                      geolocalização.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono text-teal-200">q</td>
                    <td className="px-3 py-2 text-slate-400">string · opcional</td>
                    <td className="px-3 py-2 text-slate-300">{'Apelido de "endereco".'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Experimentar */}
          <section>
            <h3 className="mb-2 text-sm font-bold text-white">Experimentar</h3>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                value={endereco}
                onChange={(e) => setEndereco(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && testar()}
                placeholder="Digite um endereço"
                className="w-full rounded-xl bg-black/40 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-400/60"
                data-testid="api-docs-input"
              />
              <button
                onClick={testar}
                disabled={loading || !endereco.trim()}
                className="flex shrink-0 items-center justify-center gap-1.5 rounded-xl bg-teal-400 px-4 py-2 text-sm font-semibold text-slate-900 transition-all hover:bg-teal-300 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="api-docs-run-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                Enviar
              </button>
            </div>
            {erroMsg && (
              <p className="mt-2 text-sm text-rose-300">{erroMsg}</p>
            )}
            {resultado && (
              <pre className="mt-3 max-h-72 overflow-auto rounded-xl bg-black/50 p-3 font-mono text-xs leading-relaxed text-slate-200">
                {resultado}
              </pre>
            )}
          </section>

          {/* Campos da resposta */}
          <section>
            <h3 className="mb-2 text-sm font-bold text-white">
              Campos da resposta
            </h3>
            <div className="overflow-hidden rounded-xl border border-white/10">
              <table className="w-full text-left text-sm">
                <tbody>
                  {campos.map(([nome, tipo, desc], i) => (
                    <tr
                      key={nome}
                      className={i < campos.length - 1 ? "border-b border-white/10" : ""}
                    >
                      <td className="whitespace-nowrap px-3 py-2 align-top font-mono text-xs text-teal-200">
                        {nome}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 align-top text-xs text-slate-400">
                        {tipo}
                      </td>
                      <td className="px-3 py-2 align-top text-xs text-slate-300">
                        {desc}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </motion.div>
    </motion.div>
  );
}
